import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Activity } from "lucide-react";
import { wheelLean } from "../lib/tilt";

const Row = ({ k, v, ok, testId }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-white/5 font-mono text-xs" data-testid={testId}>
    <span className="text-slate-400">{k}</span>
    <span className={ok === false ? "text-red-400" : ok ? "text-emerald-400" : "text-white"}>{v}</span>
  </div>
);

export default function SensorCheck() {
  const [on, setOn] = useState(false);
  const [snap, setSnap] = useState({});
  const [taps, setTaps] = useState(0);
  const [perm, setPerm] = useState("unknown");
  const s = useRef({ ori: 0, mot: 0, frames: 0, slow: 0, last: performance.now(), o: null, g: null, errors: [] });

  useEffect(() => {
    const onErr = (e) => s.current.errors.push(String(e.message || e.reason || e).slice(0, 120));
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onErr);
    navigator.permissions?.query({ name: "accelerometer" }).then((r) => setPerm(r.state)).catch(() => setPerm("n/a"));
    let raf, prev = performance.now();
    const loop = (t) => { s.current.frames++; if (t - prev > 100) s.current.slow++; prev = t; raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    const tick = setInterval(() => {
      const c = s.current, dt = (performance.now() - c.last) / 1000;
      setSnap({ oriHz: Math.round(c.ori / dt), motHz: Math.round(c.mot / dt), fps: Math.round(c.frames / dt), slow: c.slow, o: c.o, g: c.g, errors: [...c.errors] });
      c.ori = 0; c.mot = 0; c.frames = 0; c.last = performance.now();
    }, 1000);
    return () => { cancelAnimationFrame(raf); clearInterval(tick); window.removeEventListener("error", onErr); window.removeEventListener("unhandledrejection", onErr); };
  }, []);

  const start = async () => {
    try {
      if (typeof DeviceOrientationEvent?.requestPermission === "function") await DeviceOrientationEvent.requestPermission();
    } catch { /* ignore */ }
    window.addEventListener("deviceorientation", (e) => { s.current.ori++; s.current.o = { b: e.beta, g: e.gamma, a: e.alpha }; });
    window.addEventListener("devicemotion", (e) => { s.current.mot++; const a = e.accelerationIncludingGravity; s.current.g = a ? { x: a.x, y: a.y, z: a.z } : null; });
    setOn(true);
  };

  const f = (v) => (v == null ? "null" : v.toFixed(1));
  const o = snap.o, g = snap.g;
  return (
    <div className="min-h-screen bg-ink text-white px-5 py-8 max-w-lg mx-auto" data-testid="sensor-check-page">
      <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400"><Activity size={14} /> Sensor check</div>
      <h1 className="font-heading font-black uppercase text-3xl mt-2">Is your phone sending tilt data?</h1>
      <p className="text-sm text-slate-400 mt-2">Tap Start, tilt the phone for a few seconds, then take a screenshot of this page.</p>
      {!on && <button onClick={start} data-testid="sensor-check-start" className="mt-5 w-full rounded-full bg-yellow-400 text-ink font-heading font-bold uppercase py-3">Start sensors</button>}
      <div className="mt-6 rounded-2xl border border-white/10 bg-surface/60 px-4 py-2">
        <Row k="Orientation events / sec" v={on ? snap.oriHz ?? 0 : "—"} ok={on ? snap.oriHz > 0 : undefined} testId="sc-ori-hz" />
        <Row k="Motion events / sec" v={on ? snap.motHz ?? 0 : "—"} ok={on ? snap.motHz > 0 : undefined} testId="sc-mot-hz" />
        <Row k="Beta / Gamma" v={o ? `${f(o.b)}° / ${f(o.g)}°` : "—"} testId="sc-angles" />
        <Row k="Gravity x / y / z" v={g ? `${f(g.x)} / ${f(g.y)} / ${f(g.z)}` : "—"} testId="sc-gravity" />
        <Row k="Wheel lean" v={o && o.b != null ? `${f(wheelLean({ beta: o.b, gamma: o.g }))}°` : "—"} testId="sc-lean" />
        <Row k="Screen speed (fps)" v={snap.fps ?? "—"} ok={snap.fps ? snap.fps >= 30 : undefined} testId="sc-fps" />
        <Row k="Stalls (>100 ms)" v={snap.slow ?? 0} ok={(snap.slow ?? 0) < 5} testId="sc-stalls" />
        <Row k="Motion permission" v={perm} ok={perm === "denied" ? false : undefined} testId="sc-perm" />
        <Row k="Errors" v={snap.errors?.length ? snap.errors.join(" | ") : "none"} ok={!snap.errors?.length} testId="sc-errors" />
      </div>
      <button onClick={() => setTaps((n) => n + 1)} data-testid="sensor-check-tap" className="mt-4 w-full rounded-full border border-white/15 py-3 font-mono text-xs uppercase tracking-widest">Tap test: {taps}</button>
      <p className="mt-4 font-mono text-[10px] text-slate-500 break-all">{navigator.userAgent}</p>
      <Link to="/app" className="mt-6 inline-block font-mono text-xs text-yellow-400 uppercase tracking-widest">← Back to app</Link>
    </div>
  );
}
